package top.wcpe.allincore.ui.runtime.theme

import top.wcpe.allincore.dsl.ir.model.ThemeDescriptor
import top.wcpe.allincore.ui.runtime.internal.collections.readOnlyCopy
import top.wcpe.allincore.ui.runtime.internal.concurrent.ThreadLocalRef
import top.wcpe.allincore.ui.runtime.reactive.BatchScope
import top.wcpe.allincore.ui.runtime.reactive.Reactive
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.concurrent.Volatile

/** 每个 IrSession 独立持有的响应式主题环境。 */
class ThemeEnvironment(
    initialLookup: ThemeLookup,
    internal val themeManager: ThemeManager,
    descriptors: List<ThemeDescriptor>?,
) {
    private val lookupSignal: Signal<ThemeLookup> = Reactive.signal(initialLookup)
    private val descriptorProjection: List<Map<String, Any?>> = buildProjection(descriptors.orEmpty())

    @Volatile
    var currentScheme: String? = themeManager.schemeOf(initialLookup)
        private set

    /** 读取当前主题 lookup，并登记响应式依赖。 */
    fun lookup(): ThemeLookup {
        markLookupUsage()
        return lookupSignal.get()
    }

    /** 返回按目录规则排序、全层只读的 DSL 投影视图。 */
    fun getDescriptorProjection(): List<Map<String, Any?>> = descriptorProjection

    /** 合法 id 在同一响应式批次内切换 scheme 与 lookup；未知 id 不产生任何修改。 */
    fun activate(scheme: String?): Boolean {
        if (!themeManager.contains(scheme)) return false
        val nextLookup = themeManager.lookupOf(scheme)
        BatchScope.enter()
        try {
            currentScheme = scheme
            lookupSignal.set(nextLookup)
        } finally {
            BatchScope.exit()
        }
        return true
    }

    companion object {
        // KMP：ThreadLocalRef 替代 java.lang.ThreadLocal
        private val lookupUsage = ThreadLocalRef { mutableListOf<Boolean>() }

        internal fun <T> trackLookupUsage(block: () -> T): Pair<T, Boolean> {
            val stack = lookupUsage.get()
            stack.add(false)
            var used = false
            try {
                val value = block()
                used = stack.last()
                return value to used
            } finally {
                stack.removeAt(stack.lastIndex)
                if (used && stack.isNotEmpty()) {
                    stack[stack.lastIndex] = true
                }
            }
        }

        private fun markLookupUsage() {
            val stack = lookupUsage.get()
            if (stack.isNotEmpty()) {
                stack[stack.lastIndex] = true
            }
        }

        private fun buildProjection(descriptors: List<ThemeDescriptor>): List<Map<String, Any?>> {
            if (descriptors.isEmpty()) return emptyList()
            val sorted = descriptors.sortedWith(
                compareBy<ThemeDescriptor> { it.group }
                    .thenBy { it.order }
                    .thenBy { it.id },
            )
            // JVM 侧需 unmodifiable 包装，Java 调用方写回时抛 UnsupportedOperationException
            return readOnlyCopy(sorted.map(::projectDescriptor))
        }

        private fun projectDescriptor(descriptor: ThemeDescriptor): Map<String, Any?> {
            val preview = readOnlyCopy(
                mapOf(
                    "primary" to descriptor.previewPrimary,
                    "surface" to descriptor.previewSurface,
                    "onSurface" to descriptor.previewOnSurface,
                ),
            )
            return readOnlyCopy(
                mapOf(
                    "id" to descriptor.id,
                    "displayName" to descriptor.displayName,
                    "group" to descriptor.group,
                    "order" to descriptor.order,
                    "preview" to preview,
                ),
            )
        }
    }
}
